import type { UsersMeResponse } from '@ionq/trpc-console';
import { createClient, type LDContext } from '@launchdarkly/react-sdk';

const IDENTIFY_TIMEOUT_SECONDS = 3;

// Starts anonymous so the bootstrap fetch overlaps the session load; the
// root loader identifies the session user before first render. streaming:
// false pins flag values to identify-time, matching the enterprise client.
export const ldClient = createClient(
    import.meta.env.VITE_LD_CLIENT_SIDE_ID,
    { kind: 'user', anonymous: true },
    { streaming: false },
);
// Start eagerly; on failure every flag falls back to its code default.
void ldClient.start();

type SessionUser = Pick<
    UsersMeResponse,
    'id' | 'email' | 'accountId' | 'organization'
>;

let identifiedKey: string | null = null;
let inflight: Promise<void> | null = null;

/**
 * Identify the session user and resolve once their flag values are ready.
 * Never throws, never blocks past the timeout (defaults win on failure).
 * The context key is the session **accountId**, not users.id — the A/B
 * router keys its LD context the same way, so targeting rules match both
 * evaluators.
 */
export function identifyLdUser(user: SessionUser): Promise<void> {
    const key = `${user.accountId}:${user.email}`;
    if (identifiedKey === key) return inflight ?? Promise.resolve();
    identifiedKey = key;

    const ldContext: LDContext = {
        kind: 'user',
        key: user.accountId,
        email: user.email,
        user_id: user.id,
        org_id: user.organization,
    };

    const request: Promise<void> = ldClient
        .identify(ldContext, { timeout: IDENTIFY_TIMEOUT_SECONDS })
        .then((result) => {
            if (result.status === 'completed') return;
            // Clear the retry guard only while this identify is the newest —
            // a superseded one must not clobber its successor's key.
            if (identifiedKey === key) identifiedKey = null;
            // No Sentry in the shell yet — a console warning is the trail.
            console.warn(
                `LaunchDarkly identify ${result.status}${
                    result.status === 'error' ? `: ${result.error.message}` : ''
                }`,
            );
        })
        .catch((error) => {
            if (identifiedKey === key) identifiedKey = null;
            console.warn('LaunchDarkly identify failed', error);
        })
        .finally(() => {
            if (inflight === request) inflight = null;
        });
    inflight = request;

    return request;
}
